// ============================================================
// Sistem geneli toplama — süper admin (uygulama sahibi) katmanı
//
// Buradaki her fonksiyon kiracı sınırını BİLEREK aşar. Meşruiyeti
// tek kaynaktan gelir: çağıran yalnızca "sistem" rolü olabilir.
// Görünüm katmanı bunu yapabilir("kiraciYonet") ile doğrular, kural
// tarafında da sistem() ile korunur.
//
// Hesaplar başka bir kiracının şubesine geçmeyi gerektirdiğinde
// subeBaglaminda() kullanılır — kiracıyı da ayarlar, aksi hâlde
// hesap sessizce yanlış şubenin verisinden yapılır.
// ============================================================

import { BUGUN, DONEM } from "../config.js";
import { subeBaglaminda, tumKiracilar } from "./context.js";
import {
  abonelikDurumu,
  aylikUcret,
  denemeKalan,
  kullanim,
  limitDurumu,
  plan,
} from "./billing.js";
import { belgeOzeti } from "./documents.js";
import { bildirilmemisOlaylar, suresiSorunluIlaclar } from "./health.js";
import { eksikRizalar } from "./kvkk.js";
import { donemSonucu } from "./expenses.js";
import { denetimIzi } from "../repo/index.js";

const gunFark = (a, b) =>
  Math.round((new Date(a + "T00:00:00Z") - new Date(b + "T00:00:00Z")) / 86400000);

// ---------- Gelir ----------

/**
 * Kiracı bazlı gelir satırı. MRR yalnızca ücretlendirilen
 * aboneliklerden sayılır — deneme henüz gelir değildir.
 */
export function gelirSatirlari() {
  return tumKiracilar().map(k => {
    const a = k.subscription ?? {};
    const u = aylikUcret(k);
    const d = abonelikDurumu(k);
    return {
      kiraci: k,
      abonelik: a,
      plan: plan(k),
      ucret: u,
      mrr: a.status === "aktif" ? u.net : 0,
      acikTutar: d.acikTutar,
      gecikmisTutar: d.gecikmisTutar,
      gecikmisAdet: d.gecikmis.length,
      denemeKalan: denemeKalan(k),
      yenilemeyeKalan: a.renewsAt ? gunFark(a.renewsAt, BUGUN) : null,
    };
  }).sort((x, y) => y.mrr - x.mrr);
}

export function gelirOzeti() {
  const satirlar = gelirSatirlari();
  const mrr = satirlar.reduce((a, x) => a + x.mrr, 0);

  const planDagilimi = {};
  for (const x of satirlar) {
    const p = x.plan.id;
    planDagilimi[p] ??= { plan: x.plan, adet: 0, mrr: 0 };
    planDagilimi[p].adet++;
    planDagilimi[p].mrr += x.mrr;
  }

  return {
    satirlar,
    mrr,
    arr: mrr * 12,
    kiraciAdet: satirlar.length,
    odeyen: satirlar.filter(x => x.abonelik.status === "aktif").length,
    denemede: satirlar.filter(x => x.abonelik.status === "deneme").length,
    askida: satirlar.filter(x => ["askida", "iptal"].includes(x.abonelik.status)).length,
    ortalamaGelir: satirlar.filter(x => x.mrr).length
      ? Math.round(mrr / satirlar.filter(x => x.mrr).length) : 0,
    acikToplam: satirlar.reduce((a, x) => a + x.acikTutar, 0),
    gecikmisToplam: satirlar.reduce((a, x) => a + x.gecikmisTutar, 0),
    planDagilimi: Object.values(planDagilimi).sort((a, b) => b.mrr - a.mrr),
  };
}

/** Yaklaşan yenilemeler ve biten denemeler — satış takvimi. */
export function takvim(gun = 60) {
  const olaylar = [];

  for (const x of gelirSatirlari()) {
    if (x.denemeKalan != null) {
      olaylar.push({
        tip: "deneme",
        kiraci: x.kiraci,
        tarih: x.abonelik.trialEndsAt,
        kalanGun: x.denemeKalan,
        tutar: x.ucret.net,
      });
    }
    if (x.yenilemeyeKalan != null && x.yenilemeyeKalan <= gun) {
      olaylar.push({
        tip: "yenileme",
        kiraci: x.kiraci,
        tarih: x.abonelik.renewsAt,
        kalanGun: x.yenilemeyeKalan,
        tutar: x.abonelik.billingCycle === "yillik" ? x.ucret.net * 12 : x.ucret.net,
      });
    }
  }

  return olaylar.sort((a, b) => a.kalanGun - b.kalanGun);
}

// ---------- Risk ----------

/**
 * Kayıp (churn) riski. Puan yükseldikçe risk artar.
 * Sinyaller ürünün kendi verisinden gelir; anket veya tahmin yok.
 */
export function riskSatirlari() {
  return gelirSatirlari().map(x => {
    const sebepler = [];
    let puan = 0;

    if (x.gecikmisAdet) {
      puan += 30 + Math.min(30, x.gecikmisAdet * 10);
      sebepler.push(`${x.gecikmisAdet} gecikmiş fatura`);
    }
    if (x.abonelik.status === "askida") { puan += 40; sebepler.push("Abonelik askıda"); }
    if (x.denemeKalan != null && x.denemeKalan <= 7) {
      puan += 25;
      sebepler.push(`Deneme ${x.denemeKalan} gün sonra bitiyor`);
    }

    const u = kullanim(x.kiraci);
    if (u.ogrenci === 0) { puan += 30; sebepler.push("Hiç öğrenci kaydı yok"); }
    else if (u.ogrenci < 10) { puan += 10; sebepler.push("Düşük kullanım"); }

    // Son 30 günde hiç değişiklik yapılmamışsa ürün kullanılmıyor demektir.
    const esik = new Date(new Date(BUGUN + "T00:00:00Z") - 30 * 86400000)
      .toISOString().slice(0, 10);
    const hareket = denetimIzi({ tenantId: x.kiraci.id, since: esik, limit: 1 }).length;
    if (!hareket) { puan += 15; sebepler.push("30 gündür kayıtlı değişiklik yok"); }

    return {
      ...x,
      puan: Math.min(100, puan),
      sebepler,
      seviye: puan >= 60 ? "yuksek" : puan >= 30 ? "orta" : "dusuk",
    };
  }).sort((a, b) => b.puan - a.puan);
}

/** Plan yükseltme fırsatı — limitine dayanmış kiracılar. */
export function yukseltmeFirsatlari() {
  return tumKiracilar().flatMap(k =>
    limitDurumu(k)
      .filter(d => d.asildi || d.yaklasiyor)
      .map(d => ({ kiraci: k, plan: plan(k), limit: d, ucret: aylikUcret(k) })))
    .sort((a, b) => (b.limit.oran ?? 0) - (a.limit.oran ?? 0));
}

// ---------- Kullanım ----------

export function kullanimSatirlari() {
  return tumKiracilar().map(k => ({
    kiraci: k,
    plan: plan(k),
    kullanim: kullanim(k),
    limitler: limitDurumu(k),
    subeler: k.branches.map(b => ({
      sube: b,
      ogrenci: b.students.length,
      personel: b.staff.length,
      sinif: b.classes.length,
      sonuc: subeBaglaminda(b.id, () => donemSonucu(b.id, DONEM)),
    })),
  })).sort((a, b) => b.kullanim.ogrenci - a.kullanim.ogrenci);
}

export function kullanimOzeti() {
  const satirlar = kullanimSatirlari();
  return {
    satirlar,
    sube: satirlar.reduce((a, x) => a + x.kullanim.sube, 0),
    ogrenci: satirlar.reduce((a, x) => a + x.kullanim.ogrenci, 0),
    personel: satirlar.reduce((a, x) => a + x.kullanim.personel, 0),
    limitAsan: satirlar.filter(x => x.limitler.some(l => l.asildi)).length,
  };
}

// ---------- Operasyonel riskler (kiracılar arası) ----------

/**
 * Sistem geneli operasyonel riskler.
 *
 * Uygulama sahibinin bunları görmesi destek içindir: bir kurumda
 * süresi dolmuş EpiPen ya da eksik KVKK rızası varsa müşteriyi
 * uyarmak ürünün sorumluluğudur.
 *
 * DİKKAT: burada çocuk adı, sağlık verisi gibi kişisel veri
 * GÖSTERİLMEZ — yalnızca sayı ve kategori döner. Uygulama sahibinin
 * müşterinin çocuk verisine erişmesi için meşru bir gerekçe yok.
 */
/**
 * Operasyonel risk verisi bu oturumda GÖRÜLEBİLİYOR mu?
 *
 * Riskler kiracının kendi kayıtlarından (belge, ilaç, olay, rıza)
 * türetiliyor ve sistem operatörü onları okuyamıyor. Görünüm bunu
 * sormadan risk listesi çizerse her kiracıyı "Temiz" gösterir —
 * kör olduğumuz yerde "sorun yok" demek, hatanın en tehlikeli türü.
 */
export const operasyonGorunur = () => tumKiracilar().some(k => k.branches?.length > 0);

export function operasyonelRiskler() {
  return tumKiracilar().map(k => {
    const subeRiskleri = k.branches.map(b => subeBaglaminda(b.id, () => {
      const belge = belgeOzeti(b.id);
      return {
        sube: b,
        belgeDolmus: belge.zorunluDolmus,
        belgeYaklasan: belge.toplam - belge.dolmus,
        ilacSorunlu: suresiSorunluIlaclar(b.id).length,
        olayBildirilmemis: bildirilmemisOlaylar(b.id).length,
        rizaEksik: eksikRizalar(b.id).length,
      };
    }));

    const topla = alan => subeRiskleri.reduce((a, x) => a + x[alan], 0);

    return {
      kiraci: k,
      subeRiskleri,
      belgeDolmus: topla("belgeDolmus"),
      ilacSorunlu: topla("ilacSorunlu"),
      olayBildirilmemis: topla("olayBildirilmemis"),
      rizaEksik: topla("rizaEksik"),
      toplam: topla("belgeDolmus") + topla("ilacSorunlu")
            + topla("olayBildirilmemis") + topla("rizaEksik"),
    };
  }).sort((a, b) => b.toplam - a.toplam);
}

// ---------- Denetim ----------

/** Sistem geneli son değişiklikler — kiracı etiketiyle. */
export function sistemDenetimIzi(limit = 40) {
  return denetimIzi({ limit }).map(m => ({
    ...m,
    kiraciAdi: tumKiracilar().find(k => k.id === m.tenantId)?.name ?? m.tenantId ?? "—",
  }));
}

// ---------- Genel bakış ----------

export function sistemOzeti() {
  const gelir = gelirOzeti();
  const kul = kullanimOzeti();
  const risk = riskSatirlari();
  const ops = operasyonelRiskler();

  return {
    gelir,
    kullanim: kul,
    risk,
    operasyon: ops,
    yuksekRisk: risk.filter(x => x.seviye === "yuksek").length,
    operasyonelToplam: ops.reduce((a, x) => a + x.toplam, 0),
    yaklasanTakvim: takvim(30),
  };
}
